import clsx from 'clsx';
import noop from 'lodash/noop';
import type { FunctionComponent } from 'react';

import { useTranslate } from '~/shared/context/use-translate';
import { SkeletonBase } from '~/ui/core/skeleton/Skeleton';
import { SidepanelHeader } from '~/ui/domain/Sidepanel/components/SidepanelHeader';

import { ContentContainer, ContentSections, FlexRowContainer, Section, SectionHeader } from '../../job-architecture-v2';
import { PositionTileFrame } from './PositionTileFrame';

const TAB_WIDTHS = ['w-[5.125rem]', 'w-[3.4375rem]', 'w-[4rem]', 'w-[6.3125rem]'] as const;
const OVERVIEW_LINES = ['overview-a', 'overview-b', 'overview-c', 'overview-d'] as const;
const TASK_LINES = [
    ['task-1-a', 'task-1-b', 'task-1-c'],
    ['task-2-a', 'task-2-b'],
    ['task-3-a', 'task-3-b', 'task-3-c'],
    ['task-4-a', 'task-4-b'],
] as const;
const SKILL_ROWS = ['skill-a', 'skill-b', 'skill-c', 'skill-d'] as const;

const TextLines: FunctionComponent<{ readonly className?: string; readonly lines: readonly string[] }> = ({
    className,
    lines,
}) => (
    <div className={clsx('flex flex-col gap-s', className)}>
        {lines.map((line) => (
            <SkeletonBase key={line} className={clsx('h-[0.875rem]', line === lines.at(-1) ? 'w-3/5' : 'w-full')} />
        ))}
    </div>
);

const PersonSkeleton: FunctionComponent<{ readonly nameWidth: string }> = ({ nameWidth }) => (
    <span className="flex h-[2.75rem] min-w-0 items-center gap-m">
        <SkeletonBase className="size-[1.75rem] shrink-0 rounded-full" />
        <span className="flex flex-col gap-xs">
            <SkeletonBase className={clsx('h-[0.875rem]', nameWidth)} />
            <SkeletonBase className="h-[0.625rem] w-[4rem]" />
        </span>
    </span>
);

/**
 * The Position tile while it loads, laid out like the loaded tile so nothing moves when it arrives: header controls,
 * breadcrumbs, title, id and people line, the version bar, the tab bar and the Overview tab's two columns.
 */
export const PositionDetailTileSkeleton: FunctionComponent<{ readonly onClose: () => void }> = ({ onClose }) => {
    const translate = useTranslate();

    return (
        <PositionTileFrame
            header={
                <SidepanelHeader
                    actions={<SkeletonBase className="h-[1.875rem] w-[4rem] rounded-full" />}
                    activeLanguage={undefined}
                    disableArrows={true}
                    isGenerating={false}
                    isLanguagePickerDisabled={true}
                    languages={[]}
                    setActiveLanguage={noop}
                    onClose={onClose}
                />
            }
            testId="position-detail-loading"
        >
            <div
                aria-busy={true}
                aria-label={translate('pages.position-management.detail.loading')}
                className="flex min-w-0 flex-col"
                role="status"
            >
                <div className="mt-3xl flex h-[1rem] items-center gap-xl">
                    <SkeletonBase className="h-[0.75rem] w-[4.75rem]" />
                    <SkeletonBase className="h-[0.75rem] w-[3.5rem]" />
                </div>
                <div className="mt-s flex h-[2.0625rem] items-center">
                    <SkeletonBase className="h-[1.5rem] w-3/5 rounded-lg" />
                </div>
                <div className="mt-s flex h-[1rem] items-center">
                    <SkeletonBase className="h-[0.625rem] w-[16rem]" />
                </div>
                <div className="mt-l flex flex-wrap items-center gap-x-3xl gap-y-s">
                    <SkeletonBase className="h-[1.75rem] w-[6rem] rounded-full" />
                    <SkeletonBase className="h-[1.75rem] w-[6.75rem] rounded-full" />
                    <span aria-hidden={true} className="mx-xs h-4xl w-px bg-border-subtle" />
                    <PersonSkeleton nameWidth="w-[5.5rem]" />
                    <PersonSkeleton nameWidth="w-[11rem]" />
                </div>
            </div>
            <ContentContainer>
                <div className="mt-12xl flex flex-col gap-xxs overflow-hidden rounded-xl bg-surface-hover p-xxs shadow-sm">
                    <div className="flex h-[2.25rem] items-center justify-between">
                        <SkeletonBase className="h-[2.25rem] w-[6.25rem] rounded-full" />
                        <SkeletonBase className="h-[2.25rem] w-[4.5rem] rounded-full" />
                    </div>
                    <div className="flex h-[2.125rem] items-center rounded-lg bg-surface-raised px-l shadow-sm">
                        <SkeletonBase className="h-[0.75rem] w-[10rem]" />
                    </div>
                </div>
                <div className="flex flex-col gap-xxl">
                    <div className="flex">
                        <div className="flex h-[2.25rem] items-center rounded-xl bg-surface-hover p-xs">
                            {TAB_WIDTHS.map((tabWidth, index) => (
                                <span
                                    key={tabWidth}
                                    className={clsx(
                                        'flex h-[1.75rem] items-center justify-center rounded-md',
                                        index === 0 && 'bg-surface-raised shadow-sm',
                                        tabWidth,
                                    )}
                                >
                                    <SkeletonBase className="h-[0.75rem] w-3/5" />
                                </span>
                            ))}
                        </div>
                    </div>
                    <FlexRowContainer>
                        <ContentSections>
                            <Section className="text-body-s">
                                <SectionHeader>
                                    {translate('pages.job-architecture-v2.job-profile-title')}
                                </SectionHeader>
                                <TextLines lines={OVERVIEW_LINES} />
                                <div className="flex flex-col gap-xxl">
                                    <SkeletonBase className="h-[1rem] w-[3rem]" />
                                    {TASK_LINES.map((lines) => (
                                        <TextLines key={lines[0]} className="pl-3xl" lines={lines} />
                                    ))}
                                </div>
                            </Section>
                            <Section>
                                <SectionHeader>
                                    {translate('pages.job-architecture-v2.job-profile-required-skills')}
                                </SectionHeader>
                                <div className="flex flex-col gap-m">
                                    <SkeletonBase className="h-[1rem] w-2/5" />
                                    {SKILL_ROWS.map((row) => (
                                        <div key={row} className="flex items-center justify-between gap-l">
                                            <SkeletonBase className="h-[2rem] max-w-[12.5rem] flex-1 rounded-lg" />
                                            <SkeletonBase className="h-[1.25rem] w-[4rem]" />
                                        </div>
                                    ))}
                                </div>
                            </Section>
                        </ContentSections>
                    </FlexRowContainer>
                </div>
            </ContentContainer>
        </PositionTileFrame>
    );
};
